import { useFormHandling } from '@pelckmans/business-components/hooks/useFormHandling';
import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import Heading from '@pelckmans/elementis/components/heading';
import { useToast } from '@pelckmans/elementis/components/toast';
import { Dispatch, FormEventHandler, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createGroupsFromCandidateGroups, fetchGroups, Group } from '../../../../api/groups';
import { SmartschoolSchool, UserSchool } from '../../../../api/schools';
import { TOAST_ID } from '../../../../constants/ToastIds';
import ModalContext from '../../../../contexts/ModalContext';
import { GroupsWithSchool, SchoolYear, sortGroups } from '../../context/MyGroupsContext';
import SchoolSearchSelector from '../../components/SchoolSearchSelector';
import SubjectSearchSelector from '../../components/SubjectSearchSelector';
import GroupsSelectorSmartschoolSchool from './components/GroupsSelectorSmartschoolSchool';

export type FormStatus = 'failed' | 'invalid';

type Props = {
  id: string;
  schoolYear: SchoolYear;
  groupsWithSchool: GroupsWithSchool | null;
  onGroupsChange: Dispatch<SetStateAction<Group[] | undefined>>;
};

type FormValues = {
  school: UserSchool | null;
  groups: string[];
  subjects: { id: string; name: string }[];
};

const FORM_ID = 'import-smartschool-group-form';

const initialFormValues: FormValues = {
  school: null,
  groups: [],
  subjects: [],
};

function ImportSmartschoolGroupModal({ id, schoolYear, groupsWithSchool, onGroupsChange }: Props) {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const { addToast } = useToast();
  const [showAlert, setShowAlert] = useState<FormStatus | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);

  const validateForm = useCallback(
    (values: FormValues) => {
      const result: Record<string, string> = {};

      const { school, groups, subjects } = values;

      if (school === null) {
        result.school = t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.SCHOOL.ERROR');
      }

      if (groups.length === 0) {
        result.groups = t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ERROR.REQUIRED');
      }

      if (subjects.length === 0) {
        result.subjects = t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.SUBJECT.ERROR');
      }

      return result;
    },
    [t],
  );
  const { formValues, touched, errors, handleChange, handleBlur, isFormValid } = useFormHandling(
    initialFormValues,
    validateForm,
  );

  const handleClose = () => {
    closeModal(id);
  };

  useEffect(() => {
    if (Object.keys(errors).length !== 0 && submitted) {
      setShowAlert('invalid');
    } else {
      setShowAlert(undefined);
    }
  }, [errors, submitted]);

  const submitGroups = async () => {
    setSubmitted(true);

    if (!isFormValid) return;

    try {
      await createGroupsFromCandidateGroups(
        schoolYear,
        formValues.groups,
        formValues.subjects.map(x => x.id),
      );
      const groups = await fetchGroups(schoolYear);

      onGroupsChange(sortGroups(groups, language));
      addToast({
        id: TOAST_ID.IMPORTED_SMARTSCHOOL_GROUPS_SUCCESS,
        variant: 'success',
        title: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.TOAST.SUCCESS.TITLE'),
        children: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.TOAST.SUCCESS.MESSAGE'),
      });
      setShowAlert(undefined);
      handleClose();
    } catch (error) {
      setShowAlert('failed');
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await submitGroups();
  };

  const handleRetry = async () => {
    await submitGroups();
  };

  const renderAlert = () => {
    if (showAlert === 'invalid') {
      return (
        <Alert prominence="danger">
          {t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ALERT.INVALID')}
        </Alert>
      );
    }
    if (showAlert === 'failed') {
      return (
        <Alert
          prominence="danger"
          actions={{
            'aria-label': t(
              'PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ALERT.FAILED.ACTION_A11Y_LABEL',
            ),
            role: 'button',
            children: (
              <Button prominence="danger" onClick={handleRetry}>
                {t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ALERT.FAILED.ACTION')}
              </Button>
            ),
          }}
        >
          {t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ALERT.FAILED.MESSAGE')}
        </Alert>
      );
    }

    return <></>;
  };

  const handleChangeSchool = (school: UserSchool | null) => {
    handleChange({ target: { name: 'school', value: school } });
    handleChange({ target: { name: 'groups', value: [] } });
  };

  return (
    <Dialog
      id={id}
      modal
      size="md"
      onClose={handleClose}
      aria-label={t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.A11Y_LABEL')}
    >
      <Dialog.Header
        title={t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.HEADER.TITLE')}
        onClose={handleClose}
        aria-label={t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.HEADER.ACTIONS.CLOSE')}
      />
      <Dialog.Body
        alertGroupProps={
          !!showAlert
            ? {
                'aria-label': t(
                  'PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ALERT.ALERT_A11Y_LABEL',
                ),
                role: undefined,
                children: renderAlert(),
              }
            : undefined
        }
      >
        <form noValidate id={FORM_ID} name={FORM_ID} onSubmit={handleSubmit} className="add-group-form">
          <Heading level={4} removeSpacing="all" className="modal-content-text-title">
            {t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.TITLE')}
          </Heading>
          <div className="form-fields">
            <SchoolSearchSelector
              value={formValues.school}
              onChange={handleChangeSchool}
              onBlur={() => handleBlur({ target: { name: 'school' } })}
              errorText={(touched.school || submitted) && errors.school ? errors.school : undefined}
              onlySmartschool
            />
            <GroupsSelectorSmartschoolSchool
              school={formValues.school as SmartschoolSchool}
              groupsWithSchool={groupsWithSchool}
              onChange={useCallback(
                (groups: string[]) => {
                  handleChange({ target: { name: 'groups', value: groups } });
                },
                [handleChange],
              )}
              errorText={(touched.groups || submitted) && errors.groups ? errors.groups : undefined}
            />
            <SubjectSearchSelector
              value={formValues.subjects}
              onChange={subjects => handleChange({ target: { name: 'subjects', value: subjects } })}
              errorText={(touched.subjects || submitted) && errors.subjects ? errors.subjects : undefined}
              onBlur={() => handleBlur({ target: { name: 'subjects' } })}
            />
          </div>
        </form>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <>
              <Button prominence="neutral" onClick={handleClose} key="cancel">
                {t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.FOOTER.ACTIONS.CANCEL')}
              </Button>
              <Button prominence="success" type="submit" form={FORM_ID} key="submit">
                {t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.FOOTER.ACTIONS.SUBMIT')}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}

export default ImportSmartschoolGroupModal;
